import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import Button from "@mui/material/Button";
import CardsTable from "@/components/CardsTable";
import VLFTextField from "@/components/Input/TextField";
import AttachmentsContainer from "@/components/AttachmentsContainer";
import VLFModal from "@/components/Modal";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";

import {MedicalAPI} from "@/api/medical";
import {AttachmentAPI} from "@/api/attachment";

import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	dateFormat,
} from "@/utils/constants";
import {getAttachment} from "@/utils/functions";

import "./index.scss";
import {useTranslation} from "react-i18next";

function MedicalRecordPage(props) {
	const {handleNotification, onlyDetails = false} = props;

	const {id} = useParams();
	const navigate = useNavigate();
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);

	const [recordData, setRecordData] = useState({});
	const [attachments, setAttachments] = useState([]);
	const [fields, setFields] = useState();

	const [entityToDelete, setEntityToDelete] = useState({});

	const getRecord = async () => {
		setLoading(true);
		try {
			const response = onlyDetails
				? await MedicalAPI.recordsList({playerId: id})
				: await MedicalAPI.recordGetById(id);

			let detailsResponse = {};

			if (onlyDetails) {
				const playerRecordId = response?.[0]?.id;
				if (playerRecordId) {
					detailsResponse = await MedicalAPI.recordGetById(playerRecordId);
				} else {
					detailsResponse = await createPlayerRecord();
				}
			}
			setFields(onlyDetails ? detailsResponse : response);
			setRecordData(onlyDetails ? detailsResponse : response);
			setLoading(false);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const createPlayerRecord = async () => {
		let body = {
			playerId: id,
			notes: "",
		};

		try {
			const response = await MedicalAPI.recordCreate(body);
			return response;
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deleteRecord = async () => {
		setLoading(true);
		try {
			await MedicalAPI.recordDelete(recordData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/medical/records");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const getRecordAttachments = async () => {
		try {
			const response = await AttachmentAPI.list("medical_record", id);
			setAttachments(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteAttachment = async () => {
		setLoading(true);
		try {
			await AttachmentAPI.delete(entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getRecordAttachments();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getRecord();
		getRecordAttachments();
	}, []);

	const handleInjuryDelete = async (item) => {
		setLoading(true);
		try {
			await MedicalAPI.injuryDelete(recordData?.id, item.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleMedicalRecord = async () => {
		setLoading(true);
		try {
			await MedicalAPI.recordUpdate(onlyDetails ? fields?.id : id, fields);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getRecord();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const mainConfiguration = [
		{
			value: recordData?.playerData?.firstName,
			label: t("name"),
			size: "col-6 col-lg-4",
		},
		{
			value: recordData?.playerData?.lastName,
			label: "Cognome",
			size: "col-6 col-lg-4",
		},
		{
			value: recordData?.injuriesIllness?.length,
			label: "N° infortuni/malattie",
			size: "col-6 col-lg-4",
		},
	];

	const columns = [
		{
			label: "Inizio",
			key: "startDate",
			customRender: (item) => {
				return moment(item.startDate).format(dateFormat);
			},
			align: "center",
			medium: true,
		},
		// {
		// 	label: "Fine",
		// 	key: "endDate",
		// 	customRender: (item) => {
		// 		return item.endDate && moment(item.endDate).year() > 1900
		// 			? moment(item.endDate).format(dateFormat)
		// 			: "---";
		// 	},
		// 	align: "center",
		// 	small: true,
		// },
		{
			label: "Diagnosi",
			key: "diagnosis",
			align: "start",
		},
		// {
		// 	label: "Causa",
		// 	key: "cause",
		// 	align: "start",
		// },
		{
			label: "Tipologia",
			key: "injuryType",
			align: "start",
			medium: true,
		},
		{
			label: "Esame",
			key: "exam",
			align: "start",
		},
		{
			label: "Dimissione medica",
			key: "medicalDischargeDate",
			customRender: (item) => {
				return item.medicalDischargeDate
					? moment(item.medicalDischargeDate).format(dateFormat)
					: "---";
			},
			align: "center",
			medium: true,
		},
	];

	const illnessColumns = [
		{
			label: "Inizio",
			key: "startDate",
			customRender: (item) => {
				return moment(item.startDate).format(dateFormat);
			},
			align: "center",
			medium: true,
		},
		// {
		// 	label: "Fine",
		// 	key: "endDate",
		// 	customRender: (item) => {
		// 		return item.endDate && moment(item.endDate).year() > 1900
		// 			? moment(item.endDate).format(dateFormat)
		// 			: "---";
		// 	},
		// 	align: "center",
		// 	small: true,
		// },
		{
			label: "Diagnosi",
			key: "diagnosis",
			align: "start",
		},
		{
			label: "Esame",
			key: "exam",
			align: "start",
		},
		{
			label: "Dimissione medica",
			key: "medicalDischargeDate",
			customRender: (item) => {
				return item.medicalDischargeDate
					? moment(item.medicalDischargeDate).format(dateFormat)
					: "---";
			},
			align: "center",
			medium: true,
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/medical/records"),
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleMedicalRecord,
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => {
				setEntityToDelete({type: "record", id});
				setOpenHandleConfirm(true);
			},
			color: "error",
		},
	];

	const listConfiguration = {
		data: recordData?.injuriesIllness?.filter((r) => !r.recordType),
		actions: [
			{
				icon: <CloseIcon />,
				variant: "outlined",
				title: "Elimina",
				onClick: handleInjuryDelete,
				color: "error",
			},
		],
	};

	const illnessListConfiguration = {
		data: recordData?.injuriesIllness?.filter((r) => r.recordType),
		actions: [
			{
				icon: <CloseIcon />,
				variant: "outlined",
				title: "Elimina",
				onClick: handleInjuryDelete,
				color: "error",
			},
		],
	};

	const breadcrumbsElements = [
		{label: "Passaporti medici", path: "/medical/records"},
		{label: "/"},
	];

	const recordDetails = (
		<>
			{onlyDetails ? (
				<div className="d-flex align-items-center justify-content-end">
					<Button
						variant="contained"
						onClick={handleMedicalRecord}
						size="small"
					>
						<div className="d-flex align-items-center">
							<SaveIcon />
						</div>
					</Button>
				</div>
			) : null}
			<div className="row no-margin">
				<div className="col-12 padding-small">
					<VLFTextField
						value={fields?.physiologicalAnamnesis || ""}
						onChange={(e) => {
							setFields({...fields, physiologicalAnamnesis: e.target.value});
						}}
						label="Anamnesi fisiologica"
						size="small"
						multiline
						minRows={3}
						maxRows={3}
					/>
				</div>
				<div className="col-12 padding-small">
					<VLFTextField
						value={fields?.pathologicalAnamnesis || ""}
						onChange={(e) => {
							setFields({...fields, pathologicalAnamnesis: e.target.value});
						}}
						label="Anamnesi patologica"
						size="small"
						multiline
						minRows={3}
						maxRows={3}
					/>
				</div>
				<div className="col-12 padding-small">
					<VLFTextField
						value={fields?.traumatologicalAnamnesis || ""}
						onChange={(e) => {
							setFields({...fields, traumatologicalAnamnesis: e.target.value});
						}}
						label="Anamnesi traumatologica"
						size="small"
						multiline
						minRows={3}
						maxRows={3}
					/>
				</div>
				<div className="col-12 padding-small">
					<VLFTextField
						value={fields?.surgicalHistory || ""}
						onChange={(e) => {
							setFields({...fields, surgicalHistory: e.target.value});
						}}
						label="Storia chirurgica"
						size="small"
						multiline
						minRows={3}
						maxRows={3}
					/>
				</div>
			</div>
			<div className="d-flex align-items-center justify-content-end mt-2">
				<Button
					variant="contained"
					onClick={() => {
						navigate("/medical/records/" + recordData?.id + "/injuries/new");
					}}
					size="small"
				>
					<div className="d-flex align-items-center">
						<AddIcon />
					</div>
				</Button>
			</div>
			<div className="row no-margin">
				<div className="col-sm-12 padding-small">
					<CardsContainer
						tableComponent={
							<CardsTable
								title="Infortuni"
								fullWidth
								fullHeight={false}
								configurationColumns={columns}
								data={recordData?.injuriesIllness?.filter((r) => !r.recordType)}
								handleClick={(item) => {
									navigate(
										"/medical/records/" +
											recordData?.id +
											"/injuries/" +
											item.id,
										{state: {...item, playerData: recordData?.playerData}},
									);
								}}
								handleDelete={handleInjuryDelete}
								handleEdit={(item) => {
									navigate(
										"/medical/records/" +
											recordData?.id +
											"/injuries/" +
											item.id +
											"/edit",
										{state: {...item, playerData: recordData?.playerData}},
									);
								}}
							/>
						}
						listComponent={
							<CardsList
								title="Infortuni"
								type="medical"
								listConfiguration={listConfiguration}
							/>
						}
					/>
				</div>
			</div>
			<div className="row no-margin mt-2">
				<div className="col-sm-12 padding-small">
					<CardsContainer
						tableComponent={
							<CardsTable
								title="Malattie"
								fullWidth
								fullHeight={false}
								configurationColumns={illnessColumns}
								data={recordData?.injuriesIllness?.filter((r) => r.recordType)}
								handleClick={(item) => {
									navigate(
										"/medical/records/" +
											recordData?.id +
											"/injuries/" +
											item.id,
										{state: {...item, playerData: recordData?.playerData}},
									);
								}}
								handleDelete={handleInjuryDelete}
								handleEdit={(item) => {
									navigate(
										"/medical/records/" +
											recordData?.id +
											"/injuries/" +
											item.id +
											"/edit",
										{state: {...item, playerData: recordData?.playerData}},
									);
								}}
							/>
						}
						listComponent={
							<CardsList
								title="Malattie"
								type="medical"
								listConfiguration={illnessListConfiguration}
							/>
						}
					/>
				</div>
			</div>
			<div className="row no-margin mt-2">
				<div className="col-sm-12 padding-small">
					<VLFCard
						title="Allegati"
						content={
							<AttachmentsContainer
								entityId={id}
								entityType="medical_record"
								attachments={attachments}
								handleAdd={() => getRecordAttachments()}
								handleDownload={handleDownload}
								handleDelete={(id) => {
									setEntityToDelete({type: "attachment", id});
									setOpenHandleConfirm(true);
								}}
								handleNotification={handleNotification}
							/>
						}
					/>
				</div>
			</div>
		</>
	);

	if (onlyDetails) {
		return recordDetails;
	}

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					recordData
						? (recordData?.playerData?.firstName || "") +
							" " +
							(recordData?.playerData?.lastName || "")
						: ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={mainConfiguration} />
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
				<div className="mt-3">{recordDetails}</div>
			</div>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => {
					switch (entityToDelete?.type) {
						case "record":
							return deleteRecord();
						case "attachment":
							return deleteAttachment();
						default:
							return;
					}
				}}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
		</Page>
	);
}

export default MedicalRecordPage;
